import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ArrowRightLeft } from 'lucide-react'
import { conversionsFor, convert, routeFor, type Source, type Target } from '@/lib/convert'
import type { TaskResource } from '@/kinds/task/apply'
import { useUi } from '@/state/ui'
import { Button, Sheet, cx } from '@/components/ui'

/**
 * "Convert to…": pick a target, read what carries over, confirm. The source is
 * retired as "became X" (tasks, projects, lists) or kept and linked (pages).
 */
export const ConvertSheet = ({
  source,
  tasks,
  onClose,
}: {
  source: Source
  tasks: TaskResource[]
  onClose: () => void
}) => {
  const navigate = useNavigate()
  const flash = useUi((s) => s.showFlash)
  const options = conversionsFor(source, { tasks })
  const [choice, setChoice] = useState<Target | null>(options[0]?.to ?? null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const chosen = options.find((o) => o.to === choice)
  const run = async () => {
    if (!chosen) return
    setBusy(true)
    setError(null)
    try {
      const to = await convert(source, chosen.to, { tasks })
      onClose()
      flash(`Became ${chosen.label.toLowerCase()}`, 2000)
      const route = routeFor(to.kind)
      if (route === '/inbox') void navigate({ to: '/inbox' })
      else void navigate({ to: route, params: { id: to.id } })
    } catch (e) {
      setError((e as Error).message)
      setBusy(false)
    }
  }
  return (
    <Sheet
      open
      onClose={onClose}
      title="Convert to…"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!chosen || busy}
            onClick={() => void run()}
            data-testid="convert-go"
          >
            {busy ? 'Converting…' : chosen ? `Make it a ${chosen.label.toLowerCase()}` : 'Convert'}
          </Button>
        </>
      }
    >
      <div className="flex flex-wrap gap-1.5 mb-3" role="radiogroup" aria-label="Convert to">
        {options.map((o) => (
          <button
            key={o.to}
            type="button"
            role="radio"
            aria-checked={choice === o.to}
            onClick={() => setChoice(o.to)}
            className={cx(
              'rounded-full px-3 min-h-9 text-sm font-medium border transition-colors',
              choice === o.to
                ? 'bg-accent text-on-accent border-accent'
                : 'bg-raised text-muted border-line hover:text-fg',
            )}
            data-testid={`convert-${o.to.toLowerCase()}`}
          >
            {o.label}
          </button>
        ))}
      </div>
      {chosen && (
        <p
          className="text-sm text-fg/90 rounded-xl bg-panel/60 px-3 py-2.5 flex items-start gap-2"
          data-testid="convert-preview"
        >
          <ArrowRightLeft size={16} className="shrink-0 mt-0.5 text-accent" aria-hidden />
          <span>{chosen.preview}</span>
        </p>
      )}
      <p className="text-xs text-faint mt-3">
        {source.kind === 'Page'
          ? 'The page stays where it is and is linked to what it becomes.'
          : 'Nothing is deleted: this stays in history as “became …” and links to the new one.'}
      </p>
      {error && <p className="text-sm text-rose-300 mt-2">{error}</p>}
    </Sheet>
  )
}

/** "Became ▣ Kitchen" on a retired source, linking to what it turned into. */
export const BecameBanner = ({
  to,
  titleOf,
}: {
  to: { kind: string; id: string }
  titleOf: (id: string) => string | undefined
}) => {
  const navigate = useNavigate()
  const route = routeFor(to.kind)
  const glyph = to.kind === 'Project' ? '▣' : to.kind === 'Page' ? '📄' : to.kind === 'List' ? '☑' : '○'
  return (
    <button
      type="button"
      onClick={() =>
        route === '/inbox'
          ? void navigate({ to: '/inbox' })
          : void navigate({ to: route, params: { id: to.id } })
      }
      className="card w-full text-left px-3.5 py-2.5 mb-4 text-sm flex items-center gap-2 hover:bg-raised"
      data-testid="became-banner"
    >
      <ArrowRightLeft size={14} className="text-accent" aria-hidden />
      <span className="text-muted">Became</span>
      <span className="text-fg truncate">
        {glyph} {titleOf(to.id) ?? to.kind.toLowerCase()}
      </span>
    </button>
  )
}
